import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, IconButton, makeStyles, Tooltip } from '@material-ui/core'
import StopIcon from '@material-ui/icons/Stop'
import SendIcon from '@material-ui/icons/Send'
import ReplayIcon from '@material-ui/icons/Replay'
import AttachmentIcon from '@material-ui/icons/AttachFile'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { agents } from '../../../../../../../api/agents'
import ChatToolCall from './ChatToolCall'
import ChatCodeBlock from './ChatCodeBlock'

/**
 * Reusable designer chat panel.
 *
 * Props:
 *   agentId            agent identifier (e.g. 'ddp-designer')
 *   buildAttachments   () => attachment[] sent with each user message
 *   detectProposal     (code, language) => proposal | null
 *                      Caller decides whether an inline markdown code block
 *                      is an actionable proposal. If non-null, the block
 *                      renders as a clickable chip instead of source.
 *   applyProposal      (proposal) => boolean — invoked when user clicks the
 *                      proposal chip; return value flags the chip as applied.
 *   placeholder        input placeholder
 *   title              panel header text
 */
const useStyles = makeStyles(theme => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  header: {
    flexShrink: 0,
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-ai-200, rgba(169,88,255,0.25))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  iconBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)' }
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1.25),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minHeight: 0
  },
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: theme.spacing(3)
  },
  msg: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  msgRole: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  msgUser: { alignItems: 'flex-end' },
  msgAssistant: { alignItems: 'flex-start' },
  bubble: {
    maxWidth: '92%',
    padding: theme.spacing(0.75, 1),
    borderRadius: 'var(--border-radius-md, 8px)',
    fontSize: 'var(--text-size-sm)',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  bubbleUser: {
    backgroundColor: 'var(--color-primary-600)',
    color: 'white'
  },
  // Markdown content host for assistant messages
  markdown: {
    width: '100%',
    fontSize: 'var(--text-size-sm)',
    lineHeight: 1.5,
    color: 'var(--color-neutral-900)',
    '& p': { margin: '0 0 6px', '&:last-child': { margin: 0 } },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontSize: 'var(--text-size-sm)',
      fontWeight: 600,
      margin: '8px 0 4px',
      '&:first-child': { marginTop: 0 }
    },
    '& ul, & ol': { margin: '0 0 6px', paddingLeft: '20px' },
    '& li': { margin: '2px 0' },
    '& blockquote': {
      borderLeft: '2px solid var(--color-neutral-300)',
      paddingLeft: theme.spacing(1),
      margin: '6px 0',
      color: 'var(--color-neutral-500)',
      fontStyle: 'italic'
    },
    '& strong': { fontWeight: 600 },
    '& em': { fontStyle: 'italic' },
    '& a': {
      color: 'var(--color-primary-600)',
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' }
    },
    '& :not(pre) > code': {
      backgroundColor: 'var(--color-neutral-100)',
      padding: '1px 4px',
      borderRadius: 'var(--border-radius-sm)',
      fontFamily: 'var(--font-mono, monospace)',
      fontSize: '11px',
      color: 'var(--color-primary-700)'
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '6px 0',
      fontSize: '11px'
    },
    '& th, & td': {
      border: '1px solid var(--color-neutral-200)',
      padding: '4px 8px',
      textAlign: 'left'
    },
    '& th': { backgroundColor: 'var(--color-neutral-050)', fontWeight: 600 }
  },
  attachments: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    marginTop: '4px'
  },
  attachmentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--color-neutral-500)',
    backgroundColor: 'var(--color-neutral-100)',
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '10px'
  },
  systemMessage: {
    alignSelf: 'center',
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  errorBubble: {
    alignSelf: 'flex-start',
    padding: theme.spacing(0.75, 1),
    fontSize: '11px',
    color: 'var(--color-danger-700)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    border: '1px solid var(--color-danger-200, #fecaca)',
    borderRadius: 'var(--border-radius-sm)'
  },
  loading: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  inputArea: {
    flexShrink: 0,
    padding: theme.spacing(1),
    borderTop: '1px solid var(--color-ai-200, rgba(169,88,255,0.25))',
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    backgroundColor: 'white'
  },
  textarea: {
    flex: 1,
    minHeight: '36px',
    maxHeight: '120px',
    padding: theme.spacing(0.75),
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'var(--root-font)',
    fontSize: 'var(--text-size-sm)',
    lineHeight: 1.4,
    resize: 'none',
    outline: 'none',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' }
  },
  sendBtn: {
    padding: 6,
    color: 'var(--color-primary-600)',
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    border: '1px solid var(--color-primary-200, #bfdbfe)',
    '&:hover': { backgroundColor: 'var(--color-primary-100)' },
    '&.Mui-disabled': { color: 'var(--color-neutral-300)', backgroundColor: 'var(--color-neutral-100)' }
  },
  stopBtn: {
    padding: 6,
    color: 'var(--color-danger-600)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    border: '1px solid var(--color-danger-200, #fecaca)',
    '&:hover': { backgroundColor: 'var(--color-danger-100)' }
  }
}))

const ATTACHMENT_RE = /<attachment\b[^>]*>[\s\S]*?<\/attachment>/g
const ATTACHMENT_NAME_RE = /<attachment\b[^>]*\bname="([^"]+)"/i

function getRawText (content) {
  if (content == null) return ''
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content.map(c => {
      if (typeof c === 'string') return c
      if (c?.type === 'text') return c.text || ''
      return ''
    }).join('')
  }
  return ''
}

function getStrippedText (content) {
  return getRawText(content).replace(ATTACHMENT_RE, '').trim()
}

function getAttachmentNames (content) {
  const raw = getRawText(content)
  const names = []
  for (const block of raw.match(ATTACHMENT_RE) || []) {
    const m = ATTACHMENT_NAME_RE.exec(block)
    if (m) names.push(m[1])
  }
  return names
}

// Tool results are scoped to the immediately-following tool messages so the
// same tool name in a later turn doesn't overwrite the earlier rendering.
function getToolResultsForAssistant (messages, assistantIdx) {
  const results = {}
  for (let i = assistantIdx + 1; i < messages.length; i++) {
    const m = messages[i]
    if (m.role !== 'tool') break
    if (m.toolCallId) results[m.toolCallId] = m.content
    else if (m.name) results[m.name] = m.content
  }
  return results
}

function MarkdownContent ({ text, detectProposal, applyProposal }) {
  const classes = useStyles()
  const renderers = useMemo(() => ({
    pre ({ children }) {
      const codeEl = Array.isArray(children) ? children[0] : children
      const className = codeEl?.props?.className || ''
      const code = String(codeEl?.props?.children || '').replace(/\n$/, '')
      const match = /language-(.*)/.exec(className)
      const language = match ? match[1] : ''
      const proposal = detectProposal ? detectProposal(code, language) : null
      return (
        <ChatCodeBlock
          code={code}
          language={language}
          proposal={proposal}
          onApplyProposal={proposal && applyProposal ? applyProposal : null}
        />
      )
    }
  }), [detectProposal, applyProposal])

  return (
    <div className={classes.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={renderers} skipHtml>
        {text}
      </ReactMarkdown>
    </div>
  )
}

MarkdownContent.propTypes = {
  text: PropTypes.string.isRequired,
  detectProposal: PropTypes.func,
  applyProposal: PropTypes.func
}

function DesignerChatPanel ({
  agentId,
  buildAttachments,
  detectProposal,
  applyProposal,
  placeholder,
  title
}) {
  const classes = useStyles()
  const [threadId, setThreadId] = useState(null)
  const [draft, setDraft] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  const [pendingCancel, setPendingCancel] = useState(false)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  const invokeAgent = agents.useInvokeAgent(agentId)
  const cancelAgent = agents.useCancelAgent(agentId)
  const { data: thread } = agents.useAgentThread(threadId, { enabled: !!threadId, agentId })

  const messages = useMemo(() => thread?.messages || [], [thread])
  const status = thread?.status
  const inProgress = status === 'pending' || status === 'running' || invokeAgent.isLoading

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, inProgress])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const h = Math.min(textareaRef.current.scrollHeight, 120)
      textareaRef.current.style.height = `${h}px`
    }
  }, [draft])

  const handleSend = useCallback(async () => {
    if (!draft.trim() || inProgress) return
    const message = draft.trim()
    setDraft('')
    setErrorMessage(null)
    try {
      const attachments = buildAttachments ? buildAttachments() : []
      const result = await invokeAgent.mutateAsync({ message, threadId: threadId || undefined, attachments })
      if (result?.threadId) setThreadId(result.threadId)
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err.message || 'Failed to send message')
    }
  }, [draft, inProgress, buildAttachments, invokeAgent, threadId])

  const handleCancel = useCallback(async () => {
    if (!threadId || invokeAgent.isLoading) {
      setPendingCancel(true)
      return
    }
    try { await cancelAgent.mutateAsync({ threadId }) } catch { /* may have finished */ }
  }, [cancelAgent, threadId, invokeAgent.isLoading])

  useEffect(() => {
    if (!pendingCancel || !threadId || invokeAgent.isLoading) return
    setPendingCancel(false)
    cancelAgent.mutateAsync({ threadId }).catch(() => {})
  }, [pendingCancel, threadId, invokeAgent.isLoading, cancelAgent])

  const handleNewThread = useCallback(() => {
    setThreadId(null)
    setErrorMessage(null)
    setPendingCancel(false)
  }, [])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <span>{title}</span>
        {threadId && (
          <Tooltip title='Start a new thread' arrow>
            <span>
              <IconButton size='small' className={classes.iconBtn} onClick={handleNewThread} disabled={inProgress}>
                <ReplayIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      <div className={classes.messages}>
        {messages.length === 0 && !inProgress && (
          <div className={classes.empty}>
            Describe what you'd like to build or change. The assistant can propose changes for you to review.
          </div>
        )}

        {messages.map((msg, idx) => {
          // System messages — small centered note
          if (msg.role === 'assistant' && msg.name === 'system') {
            return (
              <div key={idx} className={classes.systemMessage}>
                {getStrippedText(msg.content)}
              </div>
            )
          }

          // Tool messages are consumed as results into the preceding assistant's tool calls
          if (msg.role === 'tool') return null

          if (msg.role === 'user') {
            const text = getStrippedText(msg.content)
            const attachments = getAttachmentNames(msg.content)
            return (
              <div key={idx} className={`${classes.msg} ${classes.msgUser}`}>
                <span className={classes.msgRole}>You</span>
                <div className={`${classes.bubble} ${classes.bubbleUser}`}>{text}</div>
                {attachments.length > 0 && (
                  <div className={classes.attachments}>
                    {attachments.map((name, i) => (
                      <span key={i} className={classes.attachmentChip}>
                        <AttachmentIcon style={{ fontSize: 10 }} />
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          }

          if (msg.role === 'assistant') {
            const text = getStrippedText(msg.content)
            const toolCalls = Array.isArray(msg.toolCalls) ? msg.toolCalls : []
            const scopedResults = toolCalls.length > 0 ? getToolResultsForAssistant(messages, idx) : {}
            return (
              <div key={idx} className={`${classes.msg} ${classes.msgAssistant}`}>
                <span className={classes.msgRole}>Assistant</span>
                {text && (
                  <MarkdownContent text={text} detectProposal={detectProposal} applyProposal={applyProposal} />
                )}
                {toolCalls.map((tc, tcIdx) => (
                  <ChatToolCall
                    key={tc.id || `${idx}-${tcIdx}-${tc.name}`}
                    toolCall={tc}
                    toolResult={scopedResults[tc.id] ?? scopedResults[tc.name]}
                  />
                ))}
              </div>
            )
          }

          return null
        })}

        {inProgress && (
          <div className={classes.loading}>
            <CircularProgress size={12} thickness={5} />
            <span>{status === 'running' ? 'Thinking…' : 'Sending…'}</span>
          </div>
        )}

        {errorMessage && <div className={classes.errorBubble}>{errorMessage}</div>}

        <div ref={messagesEndRef} />
      </div>

      <div className={classes.inputArea}>
        <textarea
          ref={textareaRef}
          className={classes.textarea}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={inProgress}
        />
        {inProgress
          ? (
            <Tooltip title='Stop' arrow>
              <IconButton className={classes.stopBtn} onClick={handleCancel} size='small'>
                <StopIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )
          : (
            <Tooltip title='Send' arrow>
              <span>
                <IconButton className={classes.sendBtn} onClick={handleSend} disabled={!draft.trim()} size='small'>
                  <SendIcon style={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
      </div>
    </div>
  )
}

DesignerChatPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  buildAttachments: PropTypes.func,
  detectProposal: PropTypes.func,
  applyProposal: PropTypes.func,
  placeholder: PropTypes.string,
  title: PropTypes.string
}

DesignerChatPanel.defaultProps = {
  buildAttachments: () => [],
  placeholder: 'Describe what you want…',
  title: 'Designer'
}

export default DesignerChatPanel
